import { useState, type FormEvent } from "react";
import type { QuoteFormData } from "@apex/shared";
import { useWidget } from "../state";
import { Input, Textarea, FormField } from "./ui/Input";
import { Button } from "./ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/Card";

export interface QuoteFormProps {
  className?: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  county?: string;
  company?: string;
  notes?: string;
}

const NOTES_MAX_LENGTH = 1000;

/** Inline green check icon for valid fields */
function ValidCheck() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="hsl(var(--apex-success))"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="apex-absolute apex-right-3 apex-top-1/2 apex--translate-y-1/2 apex-animate-scale-in"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function QuoteForm({ className }: QuoteFormProps) {
  const { state, theme, updateQuoteForm, setHoneypot, submitQuote } = useWidget();
  const { quoteForm, isSubmitting, honeypot } = state;
  const requireCompany = theme.requireCompany;
  const requireExplicitConsent = theme.requireExplicitConsent;

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  // Explicit-consent checkbox state. Disable submit until ticked when the
  // tenant has `requireExplicitConsent` set. The value is not sent to the
  // backend — its only purpose is to gate the client-side submit.
  const [explicitConsent, setExplicitConsent] = useState(false);

  // Validate a single field
  const validateField = (name: keyof QuoteFormData, value: string): string | undefined => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        if (value.trim().length < 2) return "Name must be at least 2 characters";
        break;
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email address";
        break;
      case "phone":
        if (!value.trim()) return "Phone number is required";
        if (!/^[\d\s\-+()]{7,}$/.test(value)) return "Invalid phone number";
        break;
      case "county":
        // County is required to align with backend validator (#537).
        if (!value.trim()) return "County is required";
        break;
      case "company":
        // Company is conditionally required per `theme.requireCompany` (#537).
        // Default true matches CPL B2B; tenants opt-out via WidgetThemeConfig (#541).
        if (requireCompany && !value.trim()) return "Company is required";
        break;
      case "notes":
        if (value.length > NOTES_MAX_LENGTH) {
          return `Notes must be ${NOTES_MAX_LENGTH} characters or fewer`;
        }
        break;
    }
    return undefined;
  };

  /** Check if a required field is valid and touched */
  const isFieldValid = (name: keyof FormErrors): boolean => {
    return !!touched[name] && !errors[name] && !!(quoteForm[name] as string)?.trim();
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: validateField("name", quoteForm.name || ""),
      email: validateField("email", quoteForm.email || ""),
      phone: validateField("phone", quoteForm.phone || ""),
      county: validateField("county", quoteForm.county || ""),
      company: validateField("company", quoteForm.company || ""),
      notes: validateField("notes", quoteForm.notes || ""),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // Handle field change
  const handleChange = (name: keyof QuoteFormData, value: string) => {
    updateQuoteForm({ [name]: value });

    // Clear error when user starts typing (if field was touched)
    if (touched[name] && errors[name as keyof FormErrors]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Handle field blur
  const handleBlur = (name: keyof QuoteFormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const value = quoteForm[name] || "";
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      county: true,
      company: true,
      notes: true,
    });

    if (!validateForm()) {
      return;
    }

    // Tenants requiring explicit consent (GDPR jurisdictions etc.) MUST tick
    // the checkbox before the form submits. This is purely client-side: the
    // backend has no concept of consent — the host page or tenant agreement
    // documents the lawful basis. We just gate the action.
    if (requireExplicitConsent && !explicitConsent) {
      return;
    }

    await submitQuote();
  };

  const notesValue = quoteForm.notes || "";
  const notesLength = notesValue.length;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="apex-font-display">Your Details</CardTitle>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="apex-space-y-4">
          {/*
            Anti-spam honeypot (#538). Hidden off-screen; real users
            (including screen readers) never fill this. Bots commonly do.
            Server silently drops submissions where this is non-empty.
          */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            data-testid="quote-form-honeypot"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{
              position: "absolute",
              left: "-9999px",
              height: 0,
              width: 0,
              opacity: 0,
              pointerEvents: "none",
            }}
          />

          {/* Name */}
          <FormField
            label="Full Name"
            error={touched.name ? errors.name : undefined}
            required
          >
            <div className="apex-relative">
              <Input
                type="text"
                value={quoteForm.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                placeholder="John Smith"
                error={touched.name && !!errors.name}
                disabled={isSubmitting}
                autoComplete="name"
              />
              {isFieldValid("name") && <ValidCheck />}
            </div>
          </FormField>

          {/* Email */}
          <FormField
            label="Email Address"
            error={touched.email ? errors.email : undefined}
            required
          >
            <div className="apex-relative">
              <Input
                type="email"
                value={quoteForm.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                placeholder="john@example.com"
                error={touched.email && !!errors.email}
                disabled={isSubmitting}
                autoComplete="email"
              />
              {isFieldValid("email") && <ValidCheck />}
            </div>
          </FormField>

          {/* Phone */}
          <FormField
            label="Phone Number"
            error={touched.phone ? errors.phone : undefined}
            required
          >
            <div className="apex-relative">
              <Input
                type="tel"
                value={quoteForm.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
                onBlur={() => handleBlur("phone")}
                placeholder="07700 900000"
                error={touched.phone && !!errors.phone}
                disabled={isSubmitting}
                autoComplete="tel"
              />
              {isFieldValid("phone") && <ValidCheck />}
            </div>
          </FormField>

          {/* Company (required when theme.requireCompany; default true) */}
          <FormField
            label="Company"
            error={touched.company ? errors.company : undefined}
            required={requireCompany}
          >
            <div className="apex-relative">
              <Input
                type="text"
                value={quoteForm.company || ""}
                onChange={(e) => handleChange("company", e.target.value)}
                onBlur={() => handleBlur("company")}
                placeholder={
                  requireCompany ? "Company name" : "Company name (optional)"
                }
                error={touched.company && !!errors.company}
                disabled={isSubmitting}
                autoComplete="organization"
              />
              {isFieldValid("company") && <ValidCheck />}
            </div>
          </FormField>

          {/* County/Region — required to align to backend validator (#537) */}
          <FormField
            label="County/Region"
            error={touched.county ? errors.county : undefined}
            required
          >
            <div className="apex-relative">
              <Input
                type="text"
                value={quoteForm.county || ""}
                onChange={(e) => handleChange("county", e.target.value)}
                onBlur={() => handleBlur("county")}
                placeholder="County or region"
                error={touched.county && !!errors.county}
                disabled={isSubmitting}
                autoComplete="address-level1"
              />
              {isFieldValid("county") && <ValidCheck />}
            </div>
          </FormField>

          {/* Notes (optional, max 1000 chars) (#537) */}
          <FormField
            label="Anything we should know?"
            error={touched.notes ? errors.notes : undefined}
          >
            <div>
              <Textarea
                value={notesValue}
                onChange={(e) => handleChange("notes", e.target.value)}
                onBlur={() => handleBlur("notes")}
                placeholder="Special requirements, preferred delivery date, etc."
                maxLength={NOTES_MAX_LENGTH}
                disabled={isSubmitting}
                rows={3}
                error={touched.notes && !!errors.notes}
              />
              <div className="apex-flex apex-justify-end apex-mt-1">
                <span
                  className="apex-text-xs apex-text-muted-foreground apex-tabular-nums"
                  aria-live="polite"
                >
                  {notesLength} / {NOTES_MAX_LENGTH}
                </span>
              </div>
            </div>
          </FormField>

          {/*
            Explicit-consent checkbox (#541). Rendered when the tenant has
            `requireExplicitConsent` set in WidgetThemeConfig — typically
            GDPR jurisdictions that mandate an opt-in tick rather than an
            implicit-by-submit consent. The value never leaves the browser;
            it only gates the submit button.
          */}
          {requireExplicitConsent && (
            <label
              htmlFor="apex-quote-consent"
              className="apex-flex apex-items-start apex-gap-2 apex-text-sm apex-text-muted-foreground"
            >
              <input
                id="apex-quote-consent"
                type="checkbox"
                checked={explicitConsent}
                onChange={(e) => setExplicitConsent(e.target.checked)}
                className="apex-mt-0.5 apex-shrink-0"
                disabled={isSubmitting}
              />
              <span>
                I consent to being contacted about this quote request.
              </span>
            </label>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            variant="cta"
            className="apex-w-full"
            size="lg"
            isLoading={isSubmitting}
            disabled={
              isSubmitting || (requireExplicitConsent && !explicitConsent)
            }
          >
            {isSubmitting ? "Submitting..." : "Submit quote request"}
          </Button>

          {/* Privacy Note */}
          <p className="apex-text-xs apex-text-muted-foreground apex-text-center">
            By submitting this form, you agree to be contacted about your quote request.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
